/**
 * Every current country of GeoNames `countryInfo.txt` (CC BY 4.0), by ISO 3166-1 alpha-2 code: its English name, and where
 * it sits on the globe, the mean position of its cities of 15,000+ people (`cities15000`) weighted by log2(population).
 * Latitude, longitude; `null` without such a city. The nuxt/nuxters collector writes codes from the same file.
 * A static table, not `Intl.DisplayNames`: Node and browsers name some regions differently (hydration mismatch).
 */
export const COUNTRIES: Record<string, { name: string, location: readonly [number, number] | null }> = {
  AD: { name: 'Andorra', location: [42.51, 1.53] },
  AE: { name: 'United Arab Emirates', location: [25.09, 55.34] },
  AF: { name: 'Afghanistan', location: [34.97, 67.22] },
  AG: { name: 'Antigua and Barbuda', location: [17.12, -61.84] },
  AI: { name: 'Anguilla', location: [18.22, -63.06] },
  AL: { name: 'Albania', location: [41.05, 19.87] },
  AM: { name: 'Armenia', location: [40.25, 44.65] },
  AO: { name: 'Angola', location: [-10.31, 15.06] },
  AQ: { name: 'Antarctica', location: null },
  AR: { name: 'Argentina', location: [-33.01, -61.58] },
  AS: { name: 'American Samoa', location: [-14.28, -170.70] },
  AT: { name: 'Austria', location: [47.74, 14.79] },
  AU: { name: 'Australia', location: [-34.35, 145.29] },
  AW: { name: 'Aruba', location: [12.53, -70.02] },
  AX: { name: 'Aland Islands', location: [60.10, 19.93] },
  AZ: { name: 'Azerbaijan', location: [40.33, 48.45] },
  BA: { name: 'Bosnia and Herzegovina', location: [44.35, 17.78] },
  BB: { name: 'Barbados', location: [13.11, -59.62] },
  BD: { name: 'Bangladesh', location: [23.71, 90.28] },
  BE: { name: 'Belgium', location: [50.89, 4.39] },
  BF: { name: 'Burkina Faso', location: [12.18, -1.77] },
  BG: { name: 'Bulgaria', location: [42.68, 25.09] },
  BH: { name: 'Bahrain', location: [26.17, 50.56] },
  BI: { name: 'Burundi', location: [-3.25, 29.70] },
  BJ: { name: 'Benin', location: [8.18, 2.22] },
  BL: { name: 'Saint Barthelemy', location: [17.90, -62.85] },
  BM: { name: 'Bermuda', location: [32.29, -64.78] },
  BN: { name: 'Brunei', location: [4.83, 114.75] },
  BO: { name: 'Bolivia', location: [-17.56, -65.51] },
  BQ: { name: 'Bonaire, Saint Eustatius and Saba ', location: [12.15, -68.27] },
  BR: { name: 'Brazil', location: [-15.84, -45.72] },
  BS: { name: 'Bahamas', location: [25.78, -78.01] },
  BT: { name: 'Bhutan', location: [27.24, 89.75] },
  BV: { name: 'Bouvet Island', location: null },
  BW: { name: 'Botswana', location: [-23.39, 25.96] },
  BY: { name: 'Belarus', location: [53.53, 27.63] },
  BZ: { name: 'Belize', location: [17.58, -88.50] },
  CA: { name: 'Canada', location: [48.44, -88.26] },
  CC: { name: 'Cocos Islands', location: [-12.16, 96.82] },
  CD: { name: 'Democratic Republic of the Congo', location: [-3.59, 23.58] },
  CF: { name: 'Central African Republic', location: [5.62, 18.35] },
  CG: { name: 'Republic of the Congo', location: [-2.12, 14.78] },
  CH: { name: 'Switzerland', location: [47.08, 8.01] },
  CI: { name: 'Ivory Coast', location: [6.72, -5.78] },
  CK: { name: 'Cook Islands', location: [-21.21, -159.78] },
  CL: { name: 'Chile', location: [-35.28, -71.74] },
  CM: { name: 'Cameroon', location: [5.93, 11.57] },
  CN: { name: 'China', location: [33.06, 110.84] },
  CO: { name: 'Colombia', location: [6.34, -74.95] },
  CR: { name: 'Costa Rica', location: [10.00, -84.19] },
  CU: { name: 'Cuba', location: [22.05, -79.83] },
  CV: { name: 'Cabo Verde', location: [15.91, -23.80] },
  CW: { name: 'Curacao', location: [12.11, -68.86] },
  CX: { name: 'Christmas Island', location: [-10.42, 105.68] },
  CY: { name: 'Cyprus', location: [35.01, 33.35] },
  CZ: { name: 'Czechia', location: [49.90, 15.44] },
  DE: { name: 'Germany', location: [51.05, 9.54] },
  DJ: { name: 'Djibouti', location: [11.52, 42.92] },
  DK: { name: 'Denmark', location: [55.78, 11.21] },
  DM: { name: 'Dominica', location: [15.30, -61.39] },
  DO: { name: 'Dominican Republic', location: [18.89, -70.23] },
  DZ: { name: 'Algeria', location: [35.56, 3.57] },
  EC: { name: 'Ecuador', location: [-1.60, -79.35] },
  EE: { name: 'Estonia', location: [59.22, 25.32] },
  EG: { name: 'Egypt', location: [29.45, 31.38] },
  EH: { name: 'Western Sahara', location: [26.00, -14.28] },
  ER: { name: 'Eritrea', location: [15.06, 38.89] },
  ES: { name: 'Spain', location: [39.60, -3.10] },
  ET: { name: 'Ethiopia', location: [9.30, 38.94] },
  FI: { name: 'Finland', location: [61.39, 24.85] },
  FJ: { name: 'Fiji', location: [-17.77, 178.16] },
  FK: { name: 'Falkland Islands', location: [-51.69, -57.86] },
  FM: { name: 'Micronesia', location: [6.92, 158.16] },
  FO: { name: 'Faroe Islands', location: [62.01, -6.77] },
  FR: { name: 'France', location: [47.40, 2.86] },
  GA: { name: 'Gabon', location: [-0.32, 10.96] },
  GB: { name: 'United Kingdom', location: [52.65, -1.54] },
  GD: { name: 'Grenada', location: [12.05, -61.75] },
  GE: { name: 'Georgia', location: [42.08, 43.06] },
  GF: { name: 'French Guiana', location: [5.07, -52.71] },
  GG: { name: 'Guernsey', location: [49.46, -2.54] },
  GH: { name: 'Ghana', location: [6.63, -0.98] },
  GI: { name: 'Gibraltar', location: [36.14, -5.35] },
  GL: { name: 'Greenland', location: [64.18, -51.72] },
  GM: { name: 'Gambia', location: [13.40, -16.52] },
  GN: { name: 'Guinea', location: [10.25, -11.65] },
  GP: { name: 'Guadeloupe', location: [16.21, -61.54] },
  GQ: { name: 'Equatorial Guinea', location: [2.16, 10.31] },
  GR: { name: 'Greece', location: [38.77, 23.39] },
  GS: { name: 'South Georgia and the South Sandwich Islands', location: [-54.28, -36.51] },
  GT: { name: 'Guatemala', location: [14.81, -90.77] },
  GU: { name: 'Guam', location: [13.49, 144.81] },
  GW: { name: 'Guinea-Bissau', location: [11.91, -15.40] },
  GY: { name: 'Guyana', location: [6.38, -58.00] },
  HK: { name: 'Hong Kong', location: [22.34, 114.16] },
  HM: { name: 'Heard Island and McDonald Islands', location: null },
  HN: { name: 'Honduras', location: [14.87, -87.54] },
  HR: { name: 'Croatia', location: [45.22, 16.61] },
  HT: { name: 'Haiti', location: [18.87, -72.52] },
  HU: { name: 'Hungary', location: [47.33, 19.32] },
  ID: { name: 'Indonesia', location: [-4.86, 111.47] },
  IE: { name: 'Ireland', location: [53.13, -7.09] },
  IL: { name: 'Israel', location: [32.20, 35.00] },
  IM: { name: 'Isle of Man', location: [54.15, -4.48] },
  IN: { name: 'India', location: [20.38, 78.75] },
  IO: { name: 'British Indian Ocean Territory', location: null },
  IQ: { name: 'Iraq', location: [33.95, 44.67] },
  IR: { name: 'Iran', location: [33.84, 51.64] },
  IS: { name: 'Iceland', location: [64.33, -21.54] },
  IT: { name: 'Italy', location: [42.65, 12.31] },
  JE: { name: 'Jersey', location: [49.19, -2.10] },
  JM: { name: 'Jamaica', location: [18.12, -77.21] },
  JO: { name: 'Jordan', location: [31.88, 35.91] },
  JP: { name: 'Japan', location: [35.59, 136.77] },
  KE: { name: 'Kenya', location: [-0.57, 36.88] },
  KG: { name: 'Kyrgyzstan', location: [41.55, 73.45] },
  KH: { name: 'Cambodia', location: [12.20, 104.67] },
  KI: { name: 'Kiribati', location: [1.33, 172.98] },
  KM: { name: 'Comoros', location: [-12.04, 43.78] },
  KN: { name: 'Saint Kitts and Nevis', location: [17.30, -62.72] },
  KP: { name: 'North Korea', location: [39.91, 126.97] },
  KR: { name: 'South Korea', location: [36.24, 127.56] },
  KW: { name: 'Kuwait', location: [29.25, 48.03] },
  KY: { name: 'Cayman Islands', location: [19.33, -81.38] },
  KZ: { name: 'Kazakhstan', location: [47.51, 69.31] },
  LA: { name: 'Laos', location: [18.28, 103.56] },
  LB: { name: 'Lebanon', location: [33.83, 35.69] },
  LC: { name: 'Saint Lucia', location: [14.03, -60.98] },
  LI: { name: 'Liechtenstein', location: [47.14, 9.52] },
  LK: { name: 'Sri Lanka', location: [7.23, 80.37] },
  LR: { name: 'Liberia', location: [6.54, -9.38] },
  LS: { name: 'Lesotho', location: [-29.47, 27.86] },
  LT: { name: 'Lithuania', location: [55.15, 24.05] },
  LU: { name: 'Luxembourg', location: [49.53, 6.07] },
  LV: { name: 'Latvia', location: [56.85, 24.32] },
  LY: { name: 'Libya', location: [31.42, 15.36] },
  MA: { name: 'Morocco', location: [33.07, -6.45] },
  MC: { name: 'Monaco', location: [43.74, 7.42] },
  MD: { name: 'Moldova', location: [47.16, 28.65] },
  ME: { name: 'Montenegro', location: [42.60, 19.09] },
  MF: { name: 'Saint Martin', location: [18.07, -63.08] },
  MG: { name: 'Madagascar', location: [-19.12, 47.39] },
  MH: { name: 'Marshall Islands', location: [7.09, 171.38] },
  MK: { name: 'North Macedonia', location: [41.76, 21.50] },
  ML: { name: 'Mali', location: [13.49, -6.08] },
  MM: { name: 'Myanmar', location: [19.12, 96.24] },
  MN: { name: 'Mongolia', location: [47.84, 103.48] },
  MO: { name: 'Macao', location: [22.16, 113.55] },
  MP: { name: 'Northern Mariana Islands', location: [15.21, 145.75] },
  MQ: { name: 'Martinique', location: [14.66, -60.99] },
  MR: { name: 'Mauritania', location: [17.49, -12.70] },
  MS: { name: 'Montserrat', location: [16.78, -62.21] },
  MT: { name: 'Malta', location: [35.90, 14.47] },
  MU: { name: 'Mauritius', location: [-20.21, 57.55] },
  MV: { name: 'Maldives', location: [4.18, 73.51] },
  MW: { name: 'Malawi', location: [-13.68, 34.37] },
  MX: { name: 'Mexico', location: [21.09, -100.02] },
  MY: { name: 'Malaysia', location: [3.59, 103.56] },
  MZ: { name: 'Mozambique', location: [-17.98, 36.27] },
  NA: { name: 'Namibia', location: [-20.82, 17.37] },
  NC: { name: 'New Caledonia', location: [-22.23, 166.49] },
  NE: { name: 'Niger', location: [14.13, 5.97] },
  NF: { name: 'Norfolk Island', location: [-29.05, 167.97] },
  NG: { name: 'Nigeria', location: [8.33, 6.82] },
  NI: { name: 'Nicaragua', location: [12.46, -86.01] },
  NL: { name: 'Netherlands', location: [52.09, 5.28] },
  NO: { name: 'Norway', location: [61.29, 9.69] },
  NP: { name: 'Nepal', location: [27.64, 84.62] },
  NR: { name: 'Nauru', location: [-0.55, 166.93] },
  NU: { name: 'Niue', location: [-19.05, -169.92] },
  NZ: { name: 'New Zealand', location: [-39.20, 174.61] },
  OM: { name: 'Oman', location: [23.24, 57.48] },
  PA: { name: 'Panama', location: [8.96, -80.07] },
  PE: { name: 'Peru', location: [-10.31, -76.22] },
  PF: { name: 'French Polynesia', location: [-17.58, -149.59] },
  PG: { name: 'Papua New Guinea', location: [-6.54, 147.19] },
  PH: { name: 'Philippines', location: [12.50, 122.31] },
  PK: { name: 'Pakistan', location: [30.52, 71.25] },
  PL: { name: 'Poland', location: [51.85, 19.10] },
  PM: { name: 'Saint Pierre and Miquelon', location: [46.78, -56.18] },
  PN: { name: 'Pitcairn', location: [-25.07, -130.10] },
  PR: { name: 'Puerto Rico', location: [18.31, -66.31] },
  PS: { name: 'Palestinian Territory', location: [31.72, 34.94] },
  PT: { name: 'Portugal', location: [39.48, -9.08] },
  PW: { name: 'Palau', location: [7.50, 134.62] },
  PY: { name: 'Paraguay', location: [-25.15, -57.10] },
  QA: { name: 'Qatar', location: [25.32, 51.49] },
  RE: { name: 'Reunion', location: [-21.09, 55.46] },
  RO: { name: 'Romania', location: [45.71, 25.14] },
  RS: { name: 'Serbia', location: [44.52, 20.61] },
  RU: { name: 'Russia', location: [56.72, 51.52] },
  RW: { name: 'Rwanda', location: [-2.07, 29.60] },
  SA: { name: 'Saudi Arabia', location: [24.32, 43.66] },
  SB: { name: 'Solomon Islands', location: [-9.44, 159.98] },
  SC: { name: 'Seychelles', location: [-4.62, 55.46] },
  SD: { name: 'Sudan', location: [14.18, 31.06] },
  SE: { name: 'Sweden', location: [58.96, 15.69] },
  SG: { name: 'Singapore', location: [1.35, 103.84] },
  SH: { name: 'Saint Helena', location: [-15.92, -5.72] },
  SI: { name: 'Slovenia', location: [46.16, 14.96] },
  SJ: { name: 'Svalbard and Jan Mayen', location: [78.22, 15.65] },
  SK: { name: 'Slovakia', location: [48.62, 18.77] },
  SL: { name: 'Sierra Leone', location: [8.44, -12.11] },
  SM: { name: 'San Marino', location: [43.94, 12.45] },
  SN: { name: 'Senegal', location: [14.57, -16.09] },
  SO: { name: 'Somalia', location: [5.70, 44.99] },
  SR: { name: 'Suriname', location: [5.82, -55.19] },
  SS: { name: 'South Sudan', location: [6.96, 29.47] },
  ST: { name: 'Sao Tome and Principe', location: [0.34, 6.73] },
  SV: { name: 'El Salvador', location: [13.71, -89.07] },
  SX: { name: 'Sint Maarten', location: [18.03, -63.05] },
  SY: { name: 'Syria', location: [34.90, 37.14] },
  SZ: { name: 'Eswatini', location: [-26.42, 31.24] },
  TC: { name: 'Turks and Caicos Islands', location: [21.64, -71.75] },
  TD: { name: 'Chad', location: [11.41, 17.43] },
  TF: { name: 'French Southern Territories', location: [-49.35, 70.22] },
  TG: { name: 'Togo', location: [8.23, 1.02] },
  TH: { name: 'Thailand', location: [13.91, 100.86] },
  TJ: { name: 'Tajikistan', location: [38.79, 69.32] },
  TK: { name: 'Tokelau', location: null },
  TL: { name: 'Timor Leste', location: [-8.73, 125.73] },
  TM: { name: 'Turkmenistan', location: [39.11, 59.70] },
  TN: { name: 'Tunisia', location: [35.79, 10.15] },
  TO: { name: 'Tonga', location: [-21.14, -175.20] },
  TR: { name: 'Turkey', location: [39.30, 33.64] },
  TT: { name: 'Trinidad and Tobago', location: [10.54, -61.37] },
  TV: { name: 'Tuvalu', location: [-8.52, 179.19] },
  TW: { name: 'Taiwan', location: [24.08, 120.76] },
  TZ: { name: 'Tanzania', location: [-5.66, 35.16] },
  UA: { name: 'Ukraine', location: [49.14, 32.71] },
  UG: { name: 'Uganda', location: [0.69, 31.99] },
  UM: { name: 'United States Minor Outlying Islands', location: null },
  US: { name: 'United States', location: [39.18, -92.26] },
  UY: { name: 'Uruguay', location: [-33.68, -56.40] },
  UZ: { name: 'Uzbekistan', location: [40.64, 68.06] },
  VA: { name: 'Vatican', location: [41.90, 12.45] },
  VC: { name: 'Saint Vincent and the Grenadines', location: [13.14, -61.21] },
  VE: { name: 'Venezuela', location: [9.62, -68.15] },
  VG: { name: 'British Virgin Islands', location: [18.43, -64.62] },
  VI: { name: 'U.S. Virgin Islands', location: [18.02, -64.83] },
  VN: { name: 'Vietnam', location: [15.87, 106.37] },
  VU: { name: 'Vanuatu', location: [-17.74, 168.31] },
  WF: { name: 'Wallis and Futuna', location: [-13.28, -176.17] },
  WS: { name: 'Samoa', location: [-13.83, -171.77] },
  XK: { name: 'Kosovo', location: [42.57, 20.86] },
  YE: { name: 'Yemen', location: [14.58, 44.70] },
  YT: { name: 'Mayotte', location: [-12.77, 45.24] },
  ZA: { name: 'South Africa', location: [-28.46, 27.36] },
  ZM: { name: 'Zambia', location: [-13.53, 28.09] },
  ZW: { name: 'Zimbabwe', location: [-18.68, 30.27] }
}

/** `FR` → `France`, the code itself when unknown. */
export function countryName(code: string): string {
  return COUNTRIES[code]?.name ?? code
}

export function countryFlag(code: string): string {
  return `i-circle-flags-${code.toLowerCase()}`
}
